/**
 * SupplierPricingCard — price-break rows per supplier.
 *
 * Each row is min_qty × unit_price (3-letter currency) with a valid_from date
 * and an optional note. The "last agreed rate" is whichever row has the
 * latest valid_from. Per-SKU pricing is deferred to v1.1.
 */
import { useState } from 'react';
import { Loader2, Plus, Save, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import {
  useSupplierPricing,
  useSupplierEnrichmentMutations,
} from '../hooks/useSupplierEnrichment';

const CURRENCY_OPTIONS = ['AUD', 'USD', 'CNY', 'EUR', 'GBP'] as const;

interface Props {
  supplierId: string;
  defaultCurrency: string | null;
  canEdit: boolean;
}

interface DraftRow {
  min_qty: string;
  unit_price: string;
  currency: string;
  valid_from: string;
  note: string;
}

const today = () => new Date().toISOString().slice(0, 10);

const blankDraft = (defaultCurrency: string | null): DraftRow => ({
  min_qty: '1',
  unit_price: '',
  currency: (defaultCurrency ?? 'USD').toUpperCase(),
  valid_from: today(),
  note: '',
});

export function SupplierPricingCard({ supplierId, defaultCurrency, canEdit }: Props) {
  const query = useSupplierPricing(supplierId);
  const { upsertPricing, deletePricing } = useSupplierEnrichmentMutations();

  const [showDraft, setShowDraft] = useState(false);
  const [draft, setDraft] = useState<DraftRow>(() => blankDraft(defaultCurrency));

  const handleSave = async () => {
    const min_qty = Number.parseInt(draft.min_qty, 10);
    const unit_price = Number.parseFloat(draft.unit_price);
    const currency = draft.currency.trim().toUpperCase();

    if (!Number.isFinite(min_qty) || min_qty < 1) {
      toast({ title: 'Invalid min qty', variant: 'destructive' });
      return;
    }
    if (!Number.isFinite(unit_price) || unit_price < 0) {
      toast({ title: 'Invalid unit price', variant: 'destructive' });
      return;
    }
    if (currency.length !== 3) {
      toast({ title: 'Currency must be a 3-letter code', variant: 'destructive' });
      return;
    }

    try {
      await upsertPricing.mutateAsync({
        supplierId,
        min_qty,
        unit_price,
        currency,
        valid_from: draft.valid_from || null,
        note: draft.note.trim() || null,
      });
      setDraft(blankDraft(defaultCurrency));
      setShowDraft(false);
    } catch (err) {
      toast({
        title: 'Failed to save pricing row',
        description: err instanceof Error ? err.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (pricingId: string) => {
    try {
      await deletePricing.mutateAsync({ pricingId });
    } catch (err) {
      toast({
        title: 'Failed to delete pricing row',
        description: err instanceof Error ? err.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  const rows = query.data ?? [];
  const latest = rows[0]; // ordered by valid_from desc

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg">Pricing</CardTitle>
        {latest && (
          <span className="text-xs text-muted-foreground">
            Last agreed:{' '}
            <span className="font-medium text-foreground tabular-nums">
              {latest.currency} {latest.unit_price.toFixed(2)}
            </span>{' '}
            at qty {latest.min_qty}+
          </span>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {rows.length > 0 ? (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="h-8 text-xs">Min qty</TableHead>
                      <TableHead className="h-8 text-xs">Unit price</TableHead>
                      <TableHead className="h-8 text-xs">Currency</TableHead>
                      <TableHead className="h-8 text-xs">Valid from</TableHead>
                      <TableHead className="h-8 text-xs">Note</TableHead>
                      {canEdit && <TableHead className="h-8 w-8" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="py-1.5 text-xs tabular-nums">{row.min_qty}</TableCell>
                        <TableCell className="py-1.5 text-xs tabular-nums">
                          {row.unit_price.toFixed(2)}
                        </TableCell>
                        <TableCell className="py-1.5 text-xs">{row.currency}</TableCell>
                        <TableCell className="py-1.5 text-xs">{row.valid_from}</TableCell>
                        <TableCell className="py-1.5 text-xs text-muted-foreground truncate max-w-[200px]">
                          {row.note}
                        </TableCell>
                        {canEdit && (
                          <TableCell className="py-1.5 text-right">
                            <button
                              type="button"
                              onClick={() => handleDelete(row.id)}
                              className="text-muted-foreground hover:text-destructive"
                              aria-label="Delete pricing row"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No pricing rows yet. Add one to record an agreed rate.
              </p>
            )}

            {canEdit && (
              <>
                {showDraft ? (
                  <div className="space-y-2 rounded-md border bg-muted/20 p-3">
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      <Input
                        type="number"
                        min={1}
                        placeholder="Min qty"
                        value={draft.min_qty}
                        onChange={(e) => setDraft({ ...draft, min_qty: e.target.value })}
                        className="h-8 text-xs"
                      />
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="Unit price"
                        value={draft.unit_price}
                        onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
                        className="h-8 text-xs"
                      />
                      <Select
                        value={draft.currency}
                        onValueChange={(value) => setDraft({ ...draft, currency: value })}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCY_OPTIONS.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="date"
                        value={draft.valid_from}
                        onChange={(e) => setDraft({ ...draft, valid_from: e.target.value })}
                        className="h-8 text-xs"
                      />
                      <Input
                        placeholder="Note (optional)"
                        value={draft.note}
                        onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setShowDraft(false);
                          setDraft(blankDraft(defaultCurrency));
                        }}
                        className="h-8 px-3 text-xs"
                      >
                        <X className="h-3.5 w-3.5 mr-1" /> Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleSave}
                        disabled={upsertPricing.isPending}
                        className="h-8 px-3 text-xs"
                      >
                        {upsertPricing.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <>
                            <Save className="h-3.5 w-3.5 mr-1" /> Save row
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setShowDraft(true)}
                    className="h-8 px-3 text-xs"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add pricing row
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
